import { put, call, takeLatest, takeLeading, delay, race, take, select } from "redux-saga/effects"
import { request } from "lib/request"
import config from "react-global-configuration"
import { languages } from "lib/languages"
import { getCurrentSelectedPatientId } from "containers/Survey/services/selector"
import { FOR_PATIENT } from "models/survey/constants"
import { Action } from "types/redux"
import {
  CONTACTLESS_PAYMENT_POLL_CANCEL,
  PAYMENT_STATE,
  READER_LINK_QR_REQUEST,
  READER_CHECK_LINKED_REQUEST,
  READER_GET_DEVICE_MODELS_REQUEST,
  READER_DELETE_DEVICE_MODEL_REQUEST,
  READER_GET_DEVICE_MODELS_POLL_REQUEST,
  PaymentState,
  CONTACTLESS_PAYMENT_WAITING_STAFF_SUCCESS,
} from "./constants"
import {
  contactlessPaymentActions,
  ContactlessPaymentCreateResponse,
  ContactlessPaymentPaymentRequest,
  PaymentStatusResponse,
  ReaderLinkQRResponse,
  ReaderCheckLinkedResponse,
  ReaderGetDeviceModelsResponse,
  ReaderGetDeviceModelsApiResponse,
  DeviceModel,
} from "./actions"

const POLL_INTERVAL_MS = 2500 // = 2.5 seconds
const POLL_TIMEOUT_MS = 900000 // = 15 minutes


function* createContactlessPaymentPayment(): Generator<any, ContactlessPaymentCreateResponse, any> {
  const current_selected_patient_id = yield select(getCurrentSelectedPatientId)
  const relative_id = current_selected_patient_id === FOR_PATIENT ? null : current_selected_patient_id
  const amount = yield select(({ payment }) => payment?.price?.ophtalmologist?.price)

  const requestPayload: ContactlessPaymentPaymentRequest = {
    relative_id: relative_id ?? null,
    amount: amount ? parseFloat(String(amount).replace(',', '.')) : null
  }

  try {
    const response: ContactlessPaymentCreateResponse = yield request(config.get("patient.contactlessPayment.create"), {
      method: "POST",
      payload: requestPayload
    })
    return response

  } catch (error: any) {
    throw error
  }
}


function* getPaymentStatus(payment_intent_id: string): Generator<any, PaymentStatusResponse, any> {
  try {
    if (!payment_intent_id) {
      throw new Error("payment_intent_id is required")
    }

    const response: PaymentStatusResponse = yield request(
      `${config.get("patient.contactlessPayment.status")}?payment_intent_id=${payment_intent_id}`, {
      method: "GET",
    })

    if (!response || typeof response.state === 'undefined') {
      throw new Error("Invalid response from payment status API")
    }

    return response
  } catch (error: any) {
    throw error
  }
}

function* updatePaymentStatus(
  payment_intent_id: string,
  state: PaymentState
): Generator<any, void, any> {
  try {
    if (!payment_intent_id) {
      console.error("updatePaymentStatus called without payment_intent_id")
      return
    }
    yield request(config.get("patient.contactlessPayment.update_status"), {
      method: "POST",
      payload: {
        payment_intent_id: payment_intent_id,
        state: state
      }
    })
  } catch (error: any) {
    console.error(error, { route: 'updatePaymentStatus' })
  }
}

function* pollPaymentStatus(payment_intent_id: string): Generator<any, boolean, any> {
  if (!payment_intent_id) {
    return false
  }

  const startTime = Date.now()

  while (true) {
    const { cancelled } = yield race({
      cancelled: take(CONTACTLESS_PAYMENT_POLL_CANCEL),
      timeout: delay(POLL_INTERVAL_MS),
    })

    if (cancelled) {
      yield call(updatePaymentStatus, payment_intent_id, PAYMENT_STATE.CANCELLED)
      yield put(contactlessPaymentActions.pollCancel())
      return false
    }

    if (Date.now() - startTime > POLL_TIMEOUT_MS) {
      yield call(updatePaymentStatus, payment_intent_id, PAYMENT_STATE.EXPIRED)
      yield put(contactlessPaymentActions.pollTimeout())
      return false
    }

    try {
      const response: PaymentStatusResponse = yield call(
        getPaymentStatus,
        payment_intent_id
      )
      if (response.state === PAYMENT_STATE.CONFIRMED) {
        yield call(updatePaymentStatus, payment_intent_id, PAYMENT_STATE.CONFIRMED)
        yield put(contactlessPaymentActions.pollSuccess())
        return true
      }

      if (response.state === PAYMENT_STATE.FAILED) {
        yield call(updatePaymentStatus, payment_intent_id, PAYMENT_STATE.FAILED)
        yield put(contactlessPaymentActions.pollError(languages.paymentRefused))
        return false
      }

      if (response.state === PAYMENT_STATE.CANCELLED) {
        yield call(updatePaymentStatus, payment_intent_id, PAYMENT_STATE.CANCELLED)
        yield put(contactlessPaymentActions.pollCancel())
        return false
      }

      if (response.state === PAYMENT_STATE.EXPIRED) {
        yield call(updatePaymentStatus, payment_intent_id, PAYMENT_STATE.EXPIRED)
        yield put(contactlessPaymentActions.pollTimeout())
        return false
      }
    } catch (error: any) {
      throw error
    }
  }
}

function* contactlessPaymentFlow(): Generator<any, void, any> {
  let payment_intent_id: string | null = null

  try {
    yield put(contactlessPaymentActions.createRequest())
    const createResponse: ContactlessPaymentCreateResponse = yield call(
      createContactlessPaymentPayment
    )

    if (!createResponse || !createResponse.payment_intent_id) {
      throw new Error("Failed to create payment: missing payment_intent_id")
    }

    payment_intent_id = createResponse.payment_intent_id
    yield put(contactlessPaymentActions.createSuccess(createResponse))

    yield call(pollPaymentStatus, payment_intent_id)

  } catch (error: any) {
    console.error("Contactless Payment create error:", error)
    if (payment_intent_id) {
      yield call(updatePaymentStatus, payment_intent_id, PAYMENT_STATE.FAILED)
    }
    yield put(
      contactlessPaymentActions.createError(
        error?.message || "Erreur lors de la création du paiement"
      )
    )
  }
}

function* createReaderLinkQR(): Generator<any, ReaderLinkQRResponse, any> {
  try {
    const response: ReaderLinkQRResponse = yield request(config.get("reader.linkQR"), {
      method: "GET",
    })

    return response

  } catch (error: any) {
    throw error
  }
}

function* readerLinkQRFlow(): Generator<any, void, any> {
  try {
    const response: ReaderLinkQRResponse = yield call(createReaderLinkQR)
    yield put(contactlessPaymentActions.readerLinkQRSuccess(response))
  } catch (error: any) {
    yield put(contactlessPaymentActions.readerLinkQRError(
      error?.message || "Erreur lors de la génération du QR code"
    ))
  }
}


function* checkReaderLinked(): Generator<any, ReaderCheckLinkedResponse, any> {
  try {
    const response: ReaderCheckLinkedResponse = yield request(config.get("reader.getReadersUUIDs"), {
      method: "GET",
    })
    return response
  } catch (error: any) {
    throw error
  }
}

function* checkReaderLinkedFlow(): Generator<any, void, any> {
  try {
    const response: ReaderCheckLinkedResponse = yield call(checkReaderLinked)
    yield put(contactlessPaymentActions.readerCheckLinkedSuccess(response))
  } catch (error: any) {
    yield put(contactlessPaymentActions.readerCheckLinkedError(
      error?.message || "Erreur lors de la vérification du reader"
    ))
  }
}

function* getDeviceModels(): Generator<any, ReaderGetDeviceModelsApiResponse, any> {
  try {
    const response: ReaderGetDeviceModelsApiResponse = yield request(config.get("reader.getDeviceModels"), {
      method: "GET",
    })
    return response
  } catch (error: any) {
    console.error("Error fetching device models:", error)
    throw error
  }
}

function* getDeviceModelsFlow(): Generator<any, void, any> {
  try {
    const apiResponse: ReaderGetDeviceModelsApiResponse = yield call(getDeviceModels)
    const deviceModels: ReaderGetDeviceModelsResponse = apiResponse.device_models || []
    yield put(contactlessPaymentActions.readerGetDeviceModelsSuccess(deviceModels))
  } catch (error: any) {
    console.error("Device models flow error:", error)
    yield put(contactlessPaymentActions.readerGetDeviceModelsError(
      error?.message || "Erreur lors de la récupération des modèles de terminaux"
    ))
  }
}

function* deleteDeviceModel(deviceModel: DeviceModel): Generator<any, void, any> {
  try {
    yield request(`${config.get("reader.deleteDeviceModel")}?reader_uuid=${deviceModel.reader_uuid}`, {
      method: "DELETE",
    })
  } catch (error: any) {
    throw error
  }
}

function* deleteDeviceModelFlow({ payload }: Action<DeviceModel>): Generator<any, void, any> {
  try {
    yield call(deleteDeviceModel, payload)
    yield put(contactlessPaymentActions.readerDeleteDeviceModelSuccess())
    yield put(contactlessPaymentActions.readerGetDeviceModelsRequest())
  } catch (error: any) {
    yield put(contactlessPaymentActions.readerDeleteDeviceModelError(
      error?.message || "Erreur lors de la suppression du modèle"
    ))
  }
}

function* getDeviceModelsPollFlow(): Generator<any, void, any> {
  try {
    const apiResponse: ReaderGetDeviceModelsApiResponse = yield call(getDeviceModels)
    const newModels: ReaderGetDeviceModelsResponse = apiResponse.device_models || []
    const currentModels: ReaderGetDeviceModelsResponse = yield select(
      (state: { contactlessPayment: { deviceModels: { models: ReaderGetDeviceModelsResponse } } }) =>
        state.contactlessPayment.deviceModels.models
    )
    const hasChanged =
      currentModels.length !== newModels.length ||
      newModels.some((m, i) => !currentModels[i] || currentModels[i].reader_uuid !== m.reader_uuid)
    if (hasChanged) {
      yield put(contactlessPaymentActions.readerGetDeviceModelsPollSuccess(newModels))
    }
  } catch (_error) {}
}

export function* contactlessPaymentWatcher(): Generator {
  yield takeLatest(READER_LINK_QR_REQUEST, readerLinkQRFlow)
  yield takeLeading(CONTACTLESS_PAYMENT_WAITING_STAFF_SUCCESS, contactlessPaymentFlow)
  yield takeLatest(READER_CHECK_LINKED_REQUEST, checkReaderLinkedFlow)
  yield takeLatest(READER_GET_DEVICE_MODELS_REQUEST, getDeviceModelsFlow)
  yield takeLatest(READER_DELETE_DEVICE_MODEL_REQUEST, deleteDeviceModelFlow)
  yield takeLatest(READER_GET_DEVICE_MODELS_POLL_REQUEST, getDeviceModelsPollFlow)
}

export default contactlessPaymentWatcher
